import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import {
  EventStateManagerService,
  EventTimeLeft,
} from 'src/app/services/event-state-manager.service';
import { ScreenSize, ScreenSizeService } from 'src/app/services/screen-size.service';
import { WbDialogService } from 'src/app/services/wb-dialog.service';
import { ConfirmationModalComponent } from 'src/app/standalones/components/modals/confirmation-modal/confirmation-modal.component';
import { PencilButtonComponent } from 'src/app/standalones/components/pencil-button/pencil-button.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { SpaceRepository } from 'src/app/state/space.repository';

@Component({
  selector: 'app-end-event-blocking-modal',
  templateUrl: './end-event-blocking-modal.component.html',
  styleUrls: ['./end-event-blocking-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    ConfirmationModalComponent,
    MatMenuModule,
    PencilButtonComponent,
  ],
})
export class EndEventBlockingModalComponent {
  protected readonly IconTypes = IconTypes;
  Array = Array;

  isEndEventHovered = false;

  extendEventEnabled =
    this.spaceRepo.activeSpace?.institution?.settings?.postEventAction?.enableExtendEvent;

  extensionTimeOptions: Map<number, string> =
    this.eventStateManagerService.EVENT_EXTENSION_TIME_TO_LOCALIZED_LABEL_MAP;

  remainingSeconds$ = this.eventStateManagerService.timeLeft$.pipe(
    map((timeLeft: EventTimeLeft | undefined) => Math.max(0, timeLeft?.timeLeftSeconds ?? 0)),
  );

  localizedDialogDescription$ = this.remainingSeconds$.pipe(
    map((seconds) =>
      this.translateService.instant(
        'Your event ends in <strong>{{ seconds }}</strong> seconds. You may close this modal, but you will be redirected when your event ends.',
        { seconds },
      ),
    ),
  );

  isXsmallScreen$ = this.screenSizeService.screenSize$.pipe(
    map((screenSize) => screenSize === ScreenSize.XSMALL),
  );

  constructor(
    private dialogRef: MatDialogRef<EndEventBlockingModalComponent>,
    private wbDialogService: WbDialogService,
    private eventStateManagerService: EventStateManagerService,
    private spaceRepo: SpaceRepository,
    private translateService: TranslateService,
    private screenSizeService: ScreenSizeService,
  ) {
    this.registerDialogRef(this.wbDialogService);
  }

  /**
   * Handles the click event on the end session button.
   */
  handleEndEvent(): void {
    this.dialogRef.close(true);
  }

  /**
   * Registers the dialog reference with the WbDialogService.
   * This is necessary for handling multiple dialogs shown at the same time.
   *
   * @param wbDialogService - The WbDialogService instance to register the dialog reference with.
   */
  private registerDialogRef(wbDialogService: WbDialogService): void {
    wbDialogService.pushNewDialogRef(this.dialogRef);
  }

  extendEvent(minutes: number) {
    this.eventStateManagerService.extendEvent(minutes * 60);
    this.dialogRef.close();
  }
  close(): void {
    this.dialogRef.close(false);
  }
}
